/**
 * Nobl9JourneyFieldExtension.tsx
 * ───────────────────────────────
 * A Backstage scaffolder field extension that renders cascading dropdowns:
 *   Chapter → Macro Journey → Micro Journey
 *
 * The journey_stage slug is auto-populated when the user completes all three
 * selections and is returned as this field's value.
 *
 * Journey data is embedded as a TypeScript constant that mirrors the
 * journey-map.yaml file in the nobl9-agent repository. When journeys are added
 * or changed, both files must be kept in sync.
 *
 * ui:options in the template YAML:
 *   audience: individual   (default; extend when Employer is added)
 *
 * Field extension name: Nobl9_JourneySelect
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  FieldExtensionComponentProps,
  CustomFieldExtensionSchema,
  createScaffolderFieldExtension,
} from '@backstage/plugin-scaffolder-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Box,
  Chip,
} from '@material-ui/core';

// ─── Journey data — mirrors journey-map.yaml in nobl9-agent repo ─────────────
interface JourneyEntry {
  journey_stage: string;
  chapter: string;
  macro_journey: string;
  micro_journey: string;
}

const JOURNEY_MAP: Record<string, JourneyEntry[]> = {
  individual: [
    // ── Research ─────────────────────────────────────────────────────────────
    {
      journey_stage: 'explore-evaluate-options',
      chapter: 'Research',
      macro_journey: 'Explore & evaluate options',
      micro_journey: 'Explore & evaluate products and providers',
    },
    // ── Buy ──────────────────────────────────────────────────────────────────
    {
      journey_stage: 'complete-application',
      chapter: 'Buy',
      macro_journey: 'Decide to purchase / enroll',
      micro_journey: 'Complete application / form',
    },
    {
      journey_stage: 'enroll',
      chapter: 'Buy',
      macro_journey: 'Decide to purchase / enroll',
      micro_journey: 'Enroll',
    },
    {
      journey_stage: 'transfer-funds-buy',
      chapter: 'Buy',
      macro_journey: 'Decide to purchase / enroll',
      micro_journey: 'Transfer funds',
    },
    {
      journey_stage: 'verification',
      chapter: 'Buy',
      macro_journey: 'Decide to purchase / enroll',
      micro_journey: 'Verification',
    },
    {
      journey_stage: 'login-onboard',
      chapter: 'Buy',
      macro_journey: 'Onboard',
      micro_journey: 'Log-in',
    },
    {
      journey_stage: 'choose-investment',
      chapter: 'Buy',
      macro_journey: 'Onboard',
      micro_journey: 'Choose investment',
    },
    {
      journey_stage: 'provide-profile-info',
      chapter: 'Buy',
      macro_journey: 'Onboard',
      micro_journey: 'Provide profile & personal information',
    },
    {
      journey_stage: 'take-ownership',
      chapter: 'Buy',
      macro_journey: 'Onboard',
      micro_journey: 'Take ownership of the account',
    },
    {
      journey_stage: 'choose-contribution',
      chapter: 'Buy',
      macro_journey: 'Onboard',
      micro_journey: 'Choose contribution / deposit amount',
    },
    {
      journey_stage: 'provide-proof-insurability',
      chapter: 'Buy',
      macro_journey: 'Onboard',
      micro_journey: 'Provide proof of insurability',
    },
    // ── Manage ───────────────────────────────────────────────────────────────
    {
      journey_stage: 'check-account-balance',
      chapter: 'Manage',
      macro_journey: 'Check-in',
      micro_journey: 'Check account balance',
    },
    {
      journey_stage: 'find-documents',
      chapter: 'Manage',
      macro_journey: 'Check-in',
      micro_journey: 'Find documents / statements',
    },
    {
      journey_stage: 'review-account-performance',
      chapter: 'Manage',
      macro_journey: 'Check-in',
      micro_journey: 'Review account performance',
    },
    {
      journey_stage: 'assess-progress',
      chapter: 'Manage',
      macro_journey: 'Check-in',
      micro_journey: 'Assess progress (link accounts)',
    },
    {
      journey_stage: 'account-details',
      chapter: 'Manage',
      macro_journey: 'Check-in',
      micro_journey: 'Account details',
    },
    {
      journey_stage: 'navigation',
      chapter: 'Manage',
      macro_journey: 'Check-in',
      micro_journey: 'Navigation',
    },
    {
      journey_stage: 'non-qualified-overview',
      chapter: 'Manage',
      macro_journey: 'Check-in',
      micro_journey: 'Non-qualified overview',
    },
    {
      journey_stage: 'product-list',
      chapter: 'Manage',
      macro_journey: 'Check-in',
      micro_journey: 'Product list',
    },
    {
      journey_stage: 'update-beneficiary',
      chapter: 'Manage',
      macro_journey: 'Update information',
      micro_journey: 'Update beneficiary',
    },
    {
      journey_stage: 'update-contact-info',
      chapter: 'Manage',
      macro_journey: 'Update information',
      micro_journey: 'Update contact information',
    },
    {
      journey_stage: 'manage-preferences',
      chapter: 'Manage',
      macro_journey: 'Update information',
      micro_journey: 'Manage preferences',
    },
    {
      journey_stage: 'manage-profile',
      chapter: 'Manage',
      macro_journey: 'Update information',
      micro_journey: 'Manage My Profile',
    },
    {
      journey_stage: 'consolidate-accounts',
      chapter: 'Manage',
      macro_journey: 'Put money in',
      micro_journey: 'Consolidate accounts',
    },
    {
      journey_stage: 'change-contributions',
      chapter: 'Manage',
      macro_journey: 'Put money in',
      micro_journey: 'Change contributions / deposits',
    },
    {
      journey_stage: 'review-investment-performance',
      chapter: 'Manage',
      macro_journey: 'Review & change investments',
      micro_journey: 'Review investment performance',
    },
    {
      journey_stage: 'evaluate-investment-options',
      chapter: 'Manage',
      macro_journey: 'Review & change investments',
      micro_journey: 'Learn about & evaluate investment options',
    },
    {
      journey_stage: 'transfer-investments',
      chapter: 'Manage',
      macro_journey: 'Review & change investments',
      micro_journey: 'Transfer investments',
    },
    {
      journey_stage: 'change-investment-allocation',
      chapter: 'Manage',
      macro_journey: 'Review & change investments',
      micro_journey: 'Change investment allocation',
    },
    {
      journey_stage: 'in-service-withdrawal',
      chapter: 'Manage',
      macro_journey: 'Take money out',
      micro_journey: 'In-service withdrawal',
    },
    {
      journey_stage: 'rollover-withdrawal',
      chapter: 'Manage',
      macro_journey: 'Take money out',
      micro_journey: 'Roll-over withdrawal',
    },
    {
      journey_stage: 'hardship-withdrawal',
      chapter: 'Manage',
      macro_journey: 'Take money out',
      micro_journey: 'Hardship withdrawal',
    },
    {
      journey_stage: 'required-minimum-distribution',
      chapter: 'Manage',
      macro_journey: 'Take money out',
      micro_journey: 'Required minimum distribution',
    },
    {
      journey_stage: 'loan',
      chapter: 'Manage',
      macro_journey: 'Take money out',
      micro_journey: 'Loan',
    },
    {
      journey_stage: 'cash-distribution',
      chapter: 'Manage',
      macro_journey: 'Change jobs',
      micro_journey: 'Cash distribution',
    },
    {
      journey_stage: 'rollover-change-jobs',
      chapter: 'Manage',
      macro_journey: 'Change jobs',
      micro_journey: 'Roll-over',
    },
    {
      journey_stage: 'stay-in-plan',
      chapter: 'Manage',
      macro_journey: 'Change jobs',
      micro_journey: 'Stay in plan',
    },
    {
      journey_stage: 'set-up-retirement-income',
      chapter: 'Manage',
      macro_journey: 'Activate income',
      micro_journey: 'Set up retirement income stream',
    },
    {
      journey_stage: 'change-retirement-payments',
      chapter: 'Manage',
      macro_journey: 'Activate income',
      micro_journey: 'Change retirement payments',
    },
    {
      journey_stage: 'make-a-claim',
      chapter: 'Manage',
      macro_journey: 'Make a claim',
      micro_journey: 'Make a claim',
    },
    {
      journey_stage: 'pay-bills-premiums',
      chapter: 'Manage',
      macro_journey: 'Pay bills/premiums',
      micro_journey: 'Pay bills/premiums',
    },
    // ── Review & Renew ───────────────────────────────────────────────────────
    {
      journey_stage: 'financial-review-strategy',
      chapter: 'Review & Renew',
      macro_journey: 'Account review',
      micro_journey: 'Financial review & strategy',
    },
    {
      journey_stage: 'renew',
      chapter: 'Review & Renew',
      macro_journey: 'Renew',
      micro_journey: 'Renew',
    },
    {
      journey_stage: 'close-account',
      chapter: 'Review & Renew',
      macro_journey: 'Close account',
      micro_journey: 'Close account',
    },
  ],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function unique<T>(arr: T[]): T[] {
  return [...new Set(arr)];
}

// ─── Component ────────────────────────────────────────────────────────────────
export const Nobl9JourneyFieldSchema: CustomFieldExtensionSchema = {
  returnValue: { type: 'string' },
  uiOptions: {
    type: 'object',
    properties: {
      audience: { type: 'string', default: 'individual' },
    },
  },
};

interface Props extends FieldExtensionComponentProps<string> {}

export const Nobl9JourneyFieldComponent = ({
  onChange,
  formData,
  rawErrors,
  uiSchema,
}: Props) => {
  // Audience comes from ui:options in the template YAML; defaults to 'individual'
  const audience: string =
    (uiSchema as any)?.['ui:options']?.audience ?? 'individual';

  // Wrap in useMemo so the array reference is stable across renders
  const entries: JourneyEntry[] = useMemo(
    () => JOURNEY_MAP[audience] ?? [],
    [audience],
  );

  // Restore state from formData (back-navigation): find the matching entry by slug
  const initialEntry = useMemo(
    () =>
      formData ? entries.find(e => e.journey_stage === formData) : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [], // only on mount
  );
  const [chapter, setChapter] = useState(initialEntry?.chapter ?? '');
  const [macro, setMacro] = useState(initialEntry?.macro_journey ?? '');
  const [micro, setMicro] = useState(initialEntry?.micro_journey ?? '');
  const [slug, setSlug] = useState(formData || '');

  // Derive cascade options
  const chapters = useMemo(
    () => unique(entries.map(e => e.chapter)),
    [entries],
  );

  const macros = useMemo(
    () =>
      unique(
        entries.filter(e => e.chapter === chapter).map(e => e.macro_journey),
      ),
    [entries, chapter],
  );

  const micros = useMemo(
    () =>
      unique(
        entries
          .filter(e => e.chapter === chapter && e.macro_journey === macro)
          .map(e => e.micro_journey),
      ),
    [entries, chapter, macro],
  );

  // Resolve slug when micro is selected
  useEffect(() => {
    if (!micro) {
      setSlug('');
      onChange('');
      return;
    }
    const entry = entries.find(
      e =>
        e.chapter === chapter &&
        e.macro_journey === macro &&
        e.micro_journey === micro,
    );
    const resolved = entry?.journey_stage ?? '';
    setSlug(resolved);
    onChange(resolved);
  }, [micro, chapter, macro, entries, onChange]);

  const onChapterChange = useCallback(
    (val: string) => {
      setChapter(val);
      setMacro('');
      setMicro('');
      setSlug('');
      onChange('');
    },
    [onChange],
  );

  const onMacroChange = useCallback(
    (val: string) => {
      setMacro(val);
      setMicro('');
      setSlug('');
      onChange('');
    },
    [onChange],
  );

  const hasError = !!rawErrors?.length;

  return (
    <Box>
      {/* Chapter */}
      <FormControl fullWidth margin="dense" error={hasError && !chapter}>
        <InputLabel id="journey-chapter-label">Chapter</InputLabel>
        <Select
          labelId="journey-chapter-label"
          value={chapter}
          onChange={e => onChapterChange(e.target.value as string)}
          displayEmpty
        >
          <MenuItem value="" disabled>
            — select chapter —
          </MenuItem>
          {chapters.map(ch => (
            <MenuItem key={ch} value={ch}>
              {ch}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Macro Journey */}
      <FormControl
        fullWidth
        margin="dense"
        error={hasError && !!chapter && !macro}
        disabled={!chapter}
      >
        <InputLabel id="journey-macro-label">Macro Journey</InputLabel>
        <Select
          labelId="journey-macro-label"
          value={macro}
          onChange={e => onMacroChange(e.target.value as string)}
          displayEmpty
        >
          <MenuItem value="" disabled>
            {chapter ? '— select macro journey —' : '— select chapter first —'}
          </MenuItem>
          {macros.map(m => (
            <MenuItem key={m} value={m}>
              {m}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Micro Journey */}
      <FormControl
        fullWidth
        margin="dense"
        error={hasError && !!macro && !micro}
        disabled={!macro}
      >
        <InputLabel id="journey-micro-label">Micro Journey</InputLabel>
        <Select
          labelId="journey-micro-label"
          value={micro}
          onChange={e => setMicro(e.target.value as string)}
          displayEmpty
        >
          <MenuItem value="" disabled>
            {macro
              ? '— select micro journey —'
              : '— select macro journey first —'}
          </MenuItem>
          {micros.map(mi => (
            <MenuItem key={mi} value={mi}>
              {mi}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* journey_stage slug — shown once resolved */}
      {slug && (
        <Box mt={1} display="flex" alignItems="center" style={{ gap: 8 }}>
          <Typography variant="caption" color="textSecondary">
            journey_stage label:
          </Typography>
          <Chip
            label={slug}
            size="small"
            color="primary"
            style={{ fontFamily: 'monospace', fontWeight: 700 }}
          />
        </Box>
      )}

      {hasError && !slug && (
        <FormHelperText error>
          Please complete the Chapter → Macro Journey → Micro Journey selection.
        </FormHelperText>
      )}
    </Box>
  );
};

export const Nobl9JourneyFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'Nobl9_JourneySelect',
    component: Nobl9JourneyFieldComponent,
    schema: Nobl9JourneyFieldSchema,
  }),
);

export default Nobl9JourneyFieldExtension;
